import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { formatSgd, apiErr } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input, Label } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";
import { Layers, RefreshCw, Check, TriangleAlert } from "lucide-react";

const COLORS = { grant: "#00E65B", debt: "#00B4E6", equity: "#D4FF00" };

function StackBar({ c, capex }) {
  const seg = (v) => `${(v / capex) * 100}%`;
  return (
    <div className="flex h-2.5 rounded-sm overflow-hidden border border-border w-full">
      {c.grant > 0 && <div style={{ width: seg(c.grant), background: COLORS.grant, opacity: 0.8 }} />}
      {c.debt > 0 && <div style={{ width: seg(c.debt), background: COLORS.debt, opacity: 0.8 }} />}
      {c.equity > 0 && <div style={{ width: seg(c.equity), background: COLORS.equity, opacity: 0.8 }} />}
    </div>
  );
}

export default function CapitalStackOptimiser({ iid, open, onClose, onApplied }) {
  const [d, setD] = useState(null);
  const [rate, setRate] = useState("");
  const [tenure, setTenure] = useState("");
  const [busy, setBusy] = useState(null);

  const load = (r, t) => {
    const params = {};
    if (r !== "" && r != null) params.rate = r;
    if (t !== "" && t != null) params.tenure_months = t;
    api.get(`/susfin/optimiser/${iid}`, { params }).then((res) => {
      setD(res.data);
      if (res.data.available) {
        setRate(String(res.data.assumptions.debt_rate_pct));
        setTenure(String(res.data.assumptions.tenure_months));
      }
    }).catch((e) => toast.error(apiErr(e)));
  };
  useEffect(() => { if (open && iid) { setD(null); load(); } }, [open, iid]); // eslint-disable-line

  const apply = async (c) => {
    setBusy(c.id);
    try {
      await api.post(`/susfin/optimiser/${iid}/apply`, {
        grant_amount: c.grant, debt_amount: c.debt,
        interest_rate: c.interest_rate, tenure_months: c.tenure_months,
      });
      toast.success("Capital stack applied to the investment case");
      onApplied?.();
      onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="sf-optimiser-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-accent" /> Capital Stack Optimiser
          </DialogTitle>
          <DialogDescription>
            Cheapest grant / debt / equity mixes for {d?.initiative_name || "this investment"}, computed through your investment case engine.
          </DialogDescription>
        </DialogHeader>

        {!d && <div className="p-6 text-muted-foreground font-mono text-sm">Computing capital stacks…</div>}

        {d && !d.available && (
          <div className="border border-[#FFB800]/40 rounded-sm p-4 text-sm text-muted-foreground" data-testid="sf-optimiser-unavailable">
            {d.message}
          </div>
        )}

        {d && d.available && (
          <div className="space-y-4">
            <div className="border border-border rounded-sm p-3 space-y-2" data-testid="sf-optimiser-assumptions">
              <div className="label-eyebrow text-[10px]">Assumptions</div>
              <div className="text-xs text-muted-foreground">
                CAPEX <span className="font-mono text-foreground">{formatSgd(d.capex)}</span> · grant: {d.assumptions.grant_source}
                {d.assumptions.grant_cap > 0 && <> (<span className="font-mono">{formatSgd(d.assumptions.grant_cap)}</span>)</>} ·
                equity cost = discount rate <span className="font-mono">{d.assumptions.equity_cost_pct}%</span>
              </div>
              <div className="flex items-end gap-2 flex-wrap">
                <div className="w-32">
                  <Label className="text-[10px]">Debt rate (% p.a.)</Label>
                  <Input className="h-8 text-xs" type="number" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="sf-optimiser-rate" />
                </div>
                <div className="w-32">
                  <Label className="text-[10px]">Tenure (months)</Label>
                  <Input className="h-8 text-xs" type="number" value={tenure} onChange={(e) => setTenure(e.target.value)} data-testid="sf-optimiser-tenure" />
                </div>
                <Button size="sm" variant="outline" className="h-8" onClick={() => { setD(null); load(rate, tenure); }} data-testid="sf-optimiser-recalc">
                  <RefreshCw className="h-3.5 w-3.5" /> Recalculate
                </Button>
                <span className="text-[10px] text-muted-foreground">{d.assumptions.note}</span>
              </div>
            </div>

            <div className="space-y-2" data-testid="sf-optimiser-candidates">
              {d.candidates.map((c) => (
                <div key={c.id} className={`border rounded-sm p-3 space-y-2 ${c.id === d.recommended_id ? "border-accent" : "border-border"}`}
                  data-testid={`sf-optimiser-candidate-${c.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-muted-foreground">#{c.rank}</span>
                      <span className="font-display font-semibold text-sm">{c.label}</span>
                      {c.id === d.recommended_id && <Badge className="text-[8px] uppercase bg-accent text-accent-foreground">Cheapest</Badge>}
                      {!c.affordable && (
                        <Badge variant="outline" className="text-[8px] uppercase text-red-400 border-red-400/40">
                          <TriangleAlert className="h-2.5 w-2.5 mr-0.5" /> DSCR {c.dscr}
                        </Badge>
                      )}
                    </div>
                    <Button size="sm" variant={c.id === d.recommended_id ? "primary" : "outline"} className="h-7"
                      disabled={busy === c.id} onClick={() => apply(c)} data-testid={`sf-optimiser-apply-${c.id}`}>
                      <Check className="h-3 w-3" /> {busy === c.id ? "Applying…" : "Apply to case"}
                    </Button>
                  </div>
                  <StackBar c={c} capex={d.capex} />
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-muted-foreground">
                    <span><span className="inline-block h-2 w-2 rounded-sm mr-1" style={{ background: COLORS.grant }} />Grant <span className="font-mono">{formatSgd(c.grant)}</span></span>
                    <span><span className="inline-block h-2 w-2 rounded-sm mr-1" style={{ background: COLORS.debt }} />Debt <span className="font-mono">{formatSgd(c.debt)}</span></span>
                    <span><span className="inline-block h-2 w-2 rounded-sm mr-1" style={{ background: COLORS.equity }} />Equity <span className="font-mono">{formatSgd(c.equity)}</span></span>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 text-center">
                    {[["Eff. cost", `${c.effective_cost_pct}%`], ["Levered NPV", formatSgd(c.levered_npv)],
                      ["IRR", c.levered_irr_pct != null ? `${c.levered_irr_pct}%` : "—"],
                      ["Payback", c.payback_years != null ? `${c.payback_years}y` : "—"],
                      ["DSCR", c.dscr != null ? c.dscr : "n/a"]].map(([l, v]) => (
                      <div key={l} className="border border-border/60 rounded-sm py-1.5">
                        <div className="text-[8px] uppercase tracking-wider text-muted-foreground">{l}</div>
                        <div className="font-mono text-xs font-bold">{v}</div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-muted-foreground">{c.why}</p>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground">{d.disclaimer}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
